import { FC, useCallback, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
    DataGridPro,
    GridColDef,
    GridPaginationModel,
    GridRowClassNameParams,
    GridRowId,
    GridValidRowModel,
    gridClasses,
    useGridApiRef,
} from '@mui/x-data-grid-pro';
import { Box } from '@mui/material';
import classNames from 'classnames';

// Components, Atoms, Molecules, Organisms
import {
    AESkeletonLoadingOverlay,
    APCustomToolbar,
    APDataTableNoRowsOverlay,
    APDataTableNoSearchDataOverlay
} from './CustomDataGridComponents';
// Types
import { HexColour } from '../../../core/types/ThemeConfigTypes';
import { TypographyStyleObject } from '../../../core/types/TypographyTypes';
// Utils
import { getBodySmallBoldStyles, getBodySmallNormalStyles } from '../../../core/utils/GetTypographyStyles/GetTypographyStyles';
// Constants
import {
    DATA_GRID_ROW_DEFAULT_HEIGHT,
    PAGE_SIZE_OPTIONS,
    SKELETON_LOADER_ROWS_DUMMY_DATA
} from '../../../core/utils/Constants/Constants';

type DataTableVariant = 'stripped' | 'normal';

interface APDataTableProps {
    type?: DataTableVariant;
    dataLoading?: boolean;
    rowsData?: GridValidRowModel[];
    columnsData?: GridColDef<GridValidRowModel>[];
    pageSizeOptions?: number[];
    className?: string;
    paginationModel?: GridPaginationModel;
    DetailPanelComponent?: any;
    rowHeight?: number;
    csvFileName?: string;
    tableName?: string;
}

export const StyledDataTableWrap = styled(Box)(() => ({
    width: '100%'
}));

const StyledDataGrid = styled(DataGridPro)<any>(({ theme }) => {
    const {
        colours: {
            borders: { borderDataTable },
            backgrounds: {
                backgroundAlt,
                background
            },
            text: {
                textWeak
            }
        } } = theme;

    const BodySmallNormalFont: TypographyStyleObject = getBodySmallNormalStyles(theme);
    const BodySmallBoldFont: TypographyStyleObject = getBodySmallBoldStyles(theme);
    const ROW_BACKGROUND_COLOR: HexColour = background;

    return {
        backgroundColor: background,
        width: '100%',
        flex: '1 0 0',
        overflowX: 'auto',
        border: `1px solid ${borderDataTable}`,
        fontFamily: 'CircularXX',
        "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
            outline: "none !important"
        },
        "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
        {
            outline: "none !important"
        },
        [`& .${gridClasses.row}`]: {
            backgroundColor: backgroundAlt,
            height: 64,
            ...BodySmallNormalFont,
            '&:hover, &.Mui-hovered': {
                backgroundColor: backgroundAlt,
                '@media (hover: none)': {
                    backgroundColor: 'transparent',
                }
            },
        },
        [`& .${gridClasses.columnHeaders}`]: {
            borderBottom: 'none',
            '& div:first-child': {
                background: backgroundAlt
            }
        },
        [`& .${gridClasses.columnHeader}`]: {
            border: 'none',
            padding: '0px 16px'
        },
        [`& .${gridClasses.row}.firstCell`]: {
            paddingLeft: '24px'
        },
        [`& .${gridClasses.columnHeaderTitle}`]: {
            ...BodySmallBoldFont,
            justifyContent: 'left',
            whiteSpace: 'normal'
        },
        [`& .${gridClasses.cell}`]: {
            border: 'none',
            padding: '6px 16px',
            height: '64px',
            alignContent: 'center',
            lineHeight: '52px'
        },
        [`& .${gridClasses.virtualScroller}`]: {
            overflowX: 'auto'
        },
        [`& .${gridClasses.overlayWrapperInner}`]: {
            overflow: 'hidden'
        },
        [`& .${gridClasses.row}.even`]: {
            backgroundColor: ROW_BACKGROUND_COLOR,
        },
        [`& .${gridClasses.footerContainer}`]: {
            fontFamily: 'CircularXX'
        },
        [`& .${gridClasses.toolbarContainer}`]: {
            display: 'flex',
            padding: 16,
            alignItems: 'center'
        },
        "& .MuiTablePagination-selectLabel": {
            ...BodySmallNormalFont,
            color: textWeak
        },
        "& .MuiTablePagination-input": {
            ...BodySmallNormalFont
        },
        "& .MuiTablePagination-displayedRows": {
            ...BodySmallNormalFont
        }
    }
});

const GetCustomRowClassName = (params: GridRowClassNameParams<any>, type: string) => {
    const isStripedRowStyle: boolean = type === 'stripped';

    const relativeIndexClass: string = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';

    return classNames(
        {
            [relativeIndexClass]: isStripedRowStyle
        }
    )
}

const APDataTable: FC<APDataTableProps> = (props: APDataTableProps) => {
    const {
        type = 'normal',
        dataLoading = false,
        rowsData = [],
        columnsData = [],
        pageSizeOptions = PAGE_SIZE_OPTIONS,
        className,
        DetailPanelComponent,
        rowHeight = DATA_GRID_ROW_DEFAULT_HEIGHT,
        csvFileName,
        tableName
    } = props;

    const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState<GridRowId[]>([]);

    const apiRef = useGridApiRef();

    const finalRowData: { id: number }[] | GridValidRowModel[] = dataLoading ? SKELETON_LOADER_ROWS_DUMMY_DATA : rowsData;

    const handleDetailPanelExpandedRowIdsChange = useCallback(
        (newIds: GridRowId[]) => {
            setDetailPanelExpandedRowIds(
                newIds.length > 1 ? [newIds[newIds.length - 1]] : newIds,
            );
        },
        [],
    );

    return (
        <StyledDataTableWrap>
            <StyledDataGrid
                className={className}
                pagination
                apiRef={apiRef}
                rowHeight={rowHeight}
                autoHeight
                initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                }}
                paginationMode="client"
                pageSizeOptions={pageSizeOptions}
                loading={dataLoading}
                rows={finalRowData}
                columns={columnsData}
                disableColumnMenu
                disableRowSelectionOnClick
                disableSelectionOnClick
                disableColumnSelector
                disableDensitySelector
                data-testid="ap-data-table"
                getRowClassName={(params: GridRowClassNameParams<any>) => GetCustomRowClassName(params, type)}
                slots={{
                    toolbar: APCustomToolbar,
                    noRowsOverlay: APDataTableNoRowsOverlay,
                    noResultsOverlay: APDataTableNoSearchDataOverlay,
                    loadingOverlay: AESkeletonLoadingOverlay
                }}
                slotProps={{
                    loadingOverlay: {
                        variant: 'skeleton',
                        noRowsVariant: 'skeleton',
                    },
                    toolbar: {
                        csvFileName,
                        tableName
                    }
                }}
                getDetailPanelHeight={() => 'auto'}
                getDetailPanelContent={({ row, id }) => {
                    return (
                        <DetailPanelComponent row={row} rowId={id} apiRef={apiRef} key={id} />
                    )
                }}
                detailPanelExpandedRowIds={detailPanelExpandedRowIds}
                onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
                sx={{
                    '--DataGrid-overlayHeight': '560px'
                }}
            />
        </StyledDataTableWrap>
    );
};

export default APDataTable;
